import AnalyticsStore from '@/store/modules/AnalyticsStore';
import type { IPortfolioTree } from '@/types/IPortfolioTree';
import { isPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import { findTreeComponentByStrategyCode } from '@/utils/portfolioTree';
import type { ComputedRef, Ref } from 'vue';
import { computed, unref } from 'vue';
import { useItemUnderAnalysisGlobalState } from './useItemUnderAnalysis';
import { isStrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import type { IItemUnderAnalysis } from '@/types/IItemUnderAnalysis';
import { getIsDraftEligibleForAlgoQuantPortfolioEngine } from '@/utils/portfolioEngineEligibility';
import { useFeatureFlag } from './useFeatureFlag';

export default function (draft?: Ref<IPortfolioTree | null>): {
  /**
   * @deprecated - should be using `itemUnderAnalysis` returned from `useItemUnderAnalysis`
   */
  itemUnderAnalysis: Ref<IItemUnderAnalysis | null>;

  portfolioName: Ref<string | null>;

  /**
   * Returns the name of the strategy under analysis.
   * It's used as key that's returned by the API.
   * So we don't need to use mask here.
   */
  strategyName: Ref<string | null>;

  /**
   * First try the current subportfolio for the given symbol.
   * if it's not found in the current subportfolio tree, then it goes to the current master portfolio tree
   * and searches that
   */
  findPortfolioTreeIdByCode: (symbol: string | undefined) => string | undefined;

  /**
   * When a portfolio is marked as read-only, we must enable the read-only mode for the user.
   */
  isReadOnlyPortfolio: Ref<boolean>;

  /**
   * When a portfolio is marked as rebalance eligible, we must enable the rebalance button for the user.
   */
  isLiveRebalancingEligible: Ref<boolean>;
} {
  const { portfolioTreeUnderAnalysis } = useItemUnderAnalysisGlobalState();

  const portfolioTreeToUse = computed((): IPortfolioTree | null => {
    return draft?.value ?? portfolioTreeUnderAnalysis.value;
  });

  const itemUnderAnalysis = computed(() => AnalyticsStore.itemUnderAnalysis);

  const portfolioName = computed((): string | null => {
    if (!portfolioTreeUnderAnalysis.value) return null;

    return portfolioTreeUnderAnalysis.value.portfolioTree.name;
  });

  const strategyName = computed((): string | null => {
    if (isStrategyFactsheetResponseDTO(itemUnderAnalysis.value)) {
      return itemUnderAnalysis.value.strategy.shortName ?? itemUnderAnalysis.value.strategy.code;
    }

    return null;
  });

  const isReadOnlyPortfolio = computed(() => portfolioTreeToUse.value?.parameters?.isReadOnlyPortfolio ?? false);

  const isLiveRebalancingEligible = computed(
    () =>
      (portfolioTreeToUse.value?.apiConfig?.shouldExposePositions &&
        portfolioTreeToUse.value?.parameters?.isRebalanceEligible) ??
      false,
  );

  const findPortfolioTreeIdByCode = (symbol: string | undefined): string | undefined => {
    if (!symbol || !portfolioTreeToUse.value) return;

    const itemUnderAnalysisToUse =
      itemUnderAnalysis.value && isPortfolioTreeSubportfolio(itemUnderAnalysis.value)
        ? itemUnderAnalysis.value
        : portfolioTreeToUse.value.portfolioTree;

    if (symbol !== portfolioName.value) {
      let component = findTreeComponentByStrategyCode(itemUnderAnalysisToUse, symbol);
      if (!component && itemUnderAnalysis.value && isPortfolioTreeSubportfolio(itemUnderAnalysis.value)) {
        // if component was not found in the subporfolio under analysis, try the whole tree
        component = findTreeComponentByStrategyCode(portfolioTreeToUse.value.portfolioTree, symbol);
      }

      if (!component) return;

      return component.portfolioTreeId;
    }

    return portfolioTreeToUse.value.portfolioId;
  };

  return {
    itemUnderAnalysis,
    portfolioName,
    strategyName,
    findPortfolioTreeIdByCode,
    isReadOnlyPortfolio,
    isLiveRebalancingEligible,
  };
}

export function useGetIsDraftEligibleForAlgoQuantPortfolioEngine({
  draft,
  itemUnderAnalysisPortfolioTreeId,
}: {
  draft: Ref<IPortfolioTree | null>;
  itemUnderAnalysisPortfolioTreeId?: Ref<string | undefined>;
}): ComputedRef<boolean> {
  const { shouldUseAlgoQuant } = useFeatureFlag();
  return computed((): boolean => {
    return getIsDraftEligibleForAlgoQuantPortfolioEngine({
      draft: unref(draft),
      itemUnderAnalysisPortfolioTreeId: unref(itemUnderAnalysisPortfolioTreeId),
      shouldUseAlgoQuant: shouldUseAlgoQuant.value,
    });
  });
}
